// A recording's playback copy and its tracks (PRD-0074, slice 03): what the video page plays
// and draws. The copy is fetched by the `<video>` element itself through the ticketed URL the
// playback read answers, with byte ranges; only the JSON goes through the client.
import type { CaseRole } from './cases'
import { apiClient } from './client'
import type { DocumentFaceState } from './photos'

export interface VideoPlaybackResponse {
  duration_s: number
  fps: number
  /** Of the upright picture: the frame every track's coordinates are in. */
  width: number
  height: number
  /** `/api/v1/files/…preview.mp4?ticket=…`; null when the run made no playback copy. */
  playback_url: string | null
  playback_expires_at: string | null
  /** The time of recording the document shows (ISO 8601) and where it came from (PRD-0074). */
  recorded_at: string | null
  recorded_at_source: RecordedAtSource | null
  /** Every candidate: the stamp read in the picture, the file's own tag, what an operator set. */
  recorded_at_candidates: RecordedAtCandidates
  /** The caller's role as the read was granted it; a viewer is offered no way to set the time. */
  member_role?: CaseRole
}

export type RecordedAtSource = 'operator' | 'burned_in' | 'metadata'

export interface RecordedAtCandidates {
  burned_in: string | null
  metadata: string | null
  operator: string | null
}

export interface RecordedAtResponse {
  recorded_at: string | null
  recorded_at_source: RecordedAtSource | null
}

export interface VideoTrackFace {
  face_id: string
  state: DocumentFaceState
  person_id: string | null
  display_name: string | null
  crop_url: string | null
}

/** One pass of one person: its span, the face it became and `[t, x1, y1, x2, y2]` per analysed frame. */
export interface VideoTrack {
  track_id: number
  t_start: number
  t_end: number
  /** The time of the kept best shot; null for a pass without a face. */
  t_best: number | null
  /** Null for a pass without a usable face, or while Advanced Face Detection is off. */
  face: VideoTrackFace | null
  samples: number[][]
}

/** One plate's pass through the picture (PRD-0074 slice 07): its reading and the box per frame. */
export interface VideoPlateTrack {
  plate_id: string
  track_id: number
  text: string
  link_status: PlateLinkStatus
  t_start: number
  t_end: number
  samples: number[][]
}

export interface VideoTracksResponse {
  duration_s: number
  width: number
  height: number
  tracks: VideoTrack[]
  /** Absent from an older API. */
  plates?: VideoPlateTrack[]
}

/** Linked to the vehicle with that plate, waiting as a suggestion, or rejected by an officer. */
export type PlateLinkStatus = 'linked' | 'suggested' | 'rejected'

export interface PlateVehicle {
  id: string
  spz: string
  display_name: string | null
}

/** One plate read in the recording (SC-010, ADR-0087). */
export interface PlateReading {
  plate_id: string
  text: string
  normalised: string
  confidence: number
  /** ISO 3166-1 alpha-2 of the format the text fits; null when it fits none the engine knows. */
  country: string | null
  track_id: number
  t_start: number
  t_end: number
  t_best: number
  box: number[]
  crop_url: string | null
  frames: number
  link_status: PlateLinkStatus
  vehicle: PlateVehicle | null
}

export interface VideoPlatesResponse {
  plates: PlateReading[]
  member_role: CaseRole
}

export type PlateDecision = 'confirm' | 'reject' | 'reopen'

/** The 404 of a document that was never analysed as a video, by its code. */
export const VIDEO_NOT_ANALYSED = 'video_not_analysed'

export const videoApi = {
  playback: (caseId: string, documentId: string) =>
    apiClient.get<VideoPlaybackResponse>(`/v1/cases/${caseId}/documents/${documentId}/video`),
  tracks: (caseId: string, documentId: string) =>
    apiClient.get<VideoTracksResponse>(`/v1/cases/${caseId}/documents/${documentId}/video/tracks`),
  faceTrack: (caseId: string, documentId: string, faceId: string) =>
    apiClient.get<VideoTrack>(
      `/v1/cases/${caseId}/documents/${documentId}/video/faces/${encodeURIComponent(faceId)}/track`
    ),
  /** Every plate read in the recording, with its vehicle when linked. */
  plates: (caseId: string, documentId: string) =>
    apiClient.get<VideoPlatesResponse>(`/v1/cases/${caseId}/documents/${documentId}/video/plates`),
  /** Link a reading to its vehicle (with the number as read, when the recogniser was wrong), reject or reopen it. */
  decidePlate: (
    caseId: string,
    documentId: string,
    plateId: string,
    decision: PlateDecision,
    text?: string | null
  ) =>
    apiClient.post<PlateReading>(
      `/v1/cases/${caseId}/documents/${documentId}/video/plates/${encodeURIComponent(plateId)}/decision`,
      { decision, text: text ?? null }
    ),
  /** Set the operator's time of recording, or clear it with null so the analysis's own shows. */
  setRecordedAt: (caseId: string, documentId: string, recordedAt: string | null) =>
    apiClient.put<RecordedAtResponse>(
      `/v1/cases/${caseId}/documents/${documentId}/video/recorded-at`,
      {
        recorded_at: recordedAt,
      }
    ),
}
